/**
 * TryOnResultsService — Persistance par utilisateur des photos try-on / flat-lay
 * mémorisées pour chaque tenue.
 *
 * Backend : POST + GET /tryon/results
 * Table   : tryon_outfit_results (user_id, outfit_key, kind, image_url, updated_at)
 *
 * v1.12.743 — Persistance LOCALE (localStorage web, AsyncStorage mobile) en
 * complément du backend, pour bypasser le CORS preflight blocking sur web
 * qui empêche le GET /tryon/results de fonctionner depuis www.mytenue.com.
 *
 * Flow:
 *   1. saveResult: POST backend (best-effort) + write local + update _cache
 *   2. getAllResults: 1) memory cache, 2) local persistent storage, 3) backend GET
 *
 * Côté UI :
 *   - OutfitCard tire ces résultats pour afficher la switch Articles/Flatlay/Try-on
 *   - Default switch : 'latest'
 */

import { DeviceEventEmitter, Platform } from 'react-native';
import { getAuthToken } from './authHelpers';

const API_URL = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';
const LS_KEY = 'mytenue_tryon_results_v1';
const LS_MAX_BYTES = 2 * 1024 * 1024; // 2 MB max stockage local (URLs only, pas d'images)

// Cache mémoire des résultats (pour éviter de re-fetch à chaque render OutfitCard)
let _cache = null;
let _cacheAt = 0;
let _hydrated = false;
const CACHE_TTL_MS = 60 * 1000; // 60s

/**
 * Construit une clé stable pour une tenue à partir des IDs des items + section.
 * Doit être déterministe : même tenue → même clé.
 */
export function buildOutfitKey(outfit) {
  if (!outfit) return '';
  const section = String(outfit.section || outfit.kind || 'looks');
  const items = Array.isArray(outfit.items) ? outfit.items : [];
  const ids = items
    .map((it) => String(it?.id || it?.partner_product_id || it?.name || ''))
    .filter(Boolean)
    .sort();
  return `${section}::${ids.join('|')}`.slice(0, 160);
}

async function authHeaders() {
  const token = await getAuthToken().catch(() => null);
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

// ─── Persistance locale (web localStorage / mobile AsyncStorage) ──────
async function loadFromLocal() {
  try {
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) return JSON.parse(raw);
    } else {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      const raw = await AsyncStorage.getItem(LS_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch (e) {
    console.warn('[TryOnResultsService] loadFromLocal:', e?.message);
  }
  return {};
}

async function saveToLocal(results) {
  try {
    const str = JSON.stringify(results || {});
    if (str.length > LS_MAX_BYTES) {
      // Garde uniquement les 50 entrées les plus récentes si dépassement
      const entries = Object.entries(results)
        .sort((a, b) => (b[1]?._updated_at || 0) - (a[1]?._updated_at || 0))
        .slice(0, 50);
      const trimmed = Object.fromEntries(entries);
      const trimmedStr = JSON.stringify(trimmed);
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        localStorage.setItem(LS_KEY, trimmedStr);
      } else {
        const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
        await AsyncStorage.setItem(LS_KEY, trimmedStr);
      }
      return;
    }
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      localStorage.setItem(LS_KEY, str);
    } else {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      await AsyncStorage.setItem(LS_KEY, str);
    }
  } catch (e) {
    console.warn('[TryOnResultsService] saveToLocal:', e?.message);
  }
}

/**
 * Récupère TOUS les résultats de l'utilisateur, indexés par outfit_key.
 * Stratégie : 1) memory cache, 2) local persistent, 3) backend GET (merge si succès)
 */
export async function getAllResults({ force = false } = {}) {
  if (!force && _cache && Date.now() - _cacheAt < CACHE_TTL_MS) {
    return _cache;
  }
  // 1) Hydrater depuis le stockage local si pas encore fait (1 fois par session)
  if (!_hydrated) {
    const local = await loadFromLocal();
    _cache = local && typeof local === 'object' ? local : {};
    _hydrated = true;
    _cacheAt = Date.now();
  }
  // 2) Fetch backend (peut échouer CORS sur web). Si succès, merge.
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/tryon/results`, { method: 'GET', headers });
    if (res.ok) {
      const json = await res.json();
      const remote = json?.results || {};
      // Merge : remote prioritaire sauf si local plus récent
      const merged = { ..._cache, ...remote };
      _cache = merged;
      _cacheAt = Date.now();
      // Mettre à jour le local cache avec le résultat merge
      saveToLocal(_cache);
      return _cache;
    }
    if (res.status === 401 || res.status === 404) {
      return _cache || {};
    }
  } catch (e) {
    // CORS, network, etc. — on continue avec le cache local
  }
  return _cache || {};
}

/**
 * Récupère les résultats d'une tenue spécifique.
 */
export async function getResultsForOutfit(outfit) {
  const key = buildOutfitKey(outfit);
  if (!key) return null;
  const all = await getAllResults();
  return all[key] || null;
}

/**
 * Sauvegarde/met à jour une photo try-on ou flat-lay pour une tenue.
 * Émet 'tryon_results_updated' pour notifier les OutfitCard visibles.
 *
 * Le POST backend est best-effort : si échec (CORS, network), on persiste
 * quand même en local pour que l'UI fonctionne.
 */
export async function saveResult({ outfit, kind, imageUrl }) {
  const outfitKey = buildOutfitKey(outfit);
  if (!outfitKey) throw new Error('outfit_key invalide');
  if (kind !== 'tryon' && kind !== 'flatlay') throw new Error('kind invalide');
  if (!imageUrl || typeof imageUrl !== 'string') throw new Error('imageUrl requis');

  // 1) MAJ memory cache + local IMMÉDIATEMENT (avant le POST)
  if (!_cache) _cache = {};
  if (!_cache[outfitKey]) _cache[outfitKey] = { tryon: null, flatlay: null, latest: null };
  _cache[outfitKey][kind] = imageUrl;
  _cache[outfitKey].latest = kind;
  _cache[outfitKey]._updated_at = Date.now();
  _cacheAt = Date.now();
  saveToLocal(_cache); // fire-and-forget

  // 2) POST backend best-effort (peut échouer CORS, c'est OK)
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/tryon/results`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ outfitKey, kind, imageUrl }),
    });
    if (!res.ok) {
      console.warn(`[TryOnResultsService] POST backend HTTP ${res.status}`);
    }
  } catch (e) {
    // CORS, network, etc. — local-only mode
    console.warn('[TryOnResultsService] POST backend failed (local-only):', e?.message);
  }

  // 3) Notifier UI (toujours, même si backend a échoué)
  try {
    DeviceEventEmitter.emit('tryon_results_updated', { outfitKey, kind, imageUrl });
  } catch {}
  return { ok: true };
}

/** Invalide le cache (appel après login/logout ou refresh manuel) */
export async function clearCache() {
  _cache = null;
  _cacheAt = 0;
  _hydrated = false;
  try {
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      localStorage.removeItem(LS_KEY);
    } else {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      await AsyncStorage.removeItem(LS_KEY);
    }
  } catch {}
}
